// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { SelectedAssetsSelector } from '@advisor-app/features/portfolio';
import { assetsTypeSlice, selectAssetsByType } from '@shared/data-access/slices';
import {
  type AssetsTypeState,
  AssetTypes,
} from '@shared/data-access/interfaces/redux/assets-type-state-types';

import { selectWatchlistPositions } from '@shared/data-access/slices/tag-manager';
import { useAppSelector } from '@advisor-app/store/store';
import { type PortfolioLoaded } from '@shared/data-access/interfaces/common/portfolio-loaded';
import { type PopupsName, SectionContainer } from '@shared/ui-kit/components';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AssetItem } from './asset-item';
import { SearchAsset } from './search-asset';
import { useTranslation } from 'react-i18next';

export type AllowedAssetType = 'etf' | 'mutual_fund';

interface AssetTypeConfig {
  name: string;
  popupName: PopupsName;
  reduxType: AssetTypes;
}

interface AssetSelectorProps {
  assetType: AllowedAssetType;
}

export const AssetSelector: FC<AssetSelectorProps> = ({ assetType }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [selectedAssetsTickers, setSelectedAssetsTickers] = useState<
    PortfolioLoaded[]
  >([]);

  const assetTypeConfig: Record<AllowedAssetType, AssetTypeConfig> = useMemo(
    () => ({
      etf: {
        name: t('glossary.etf', { ns: 'glossary' }) as string,
        popupName: 'ai-builder.selectETFs',
        reduxType: AssetTypes.ETFS,
      },
      mutual_fund: {
        name: t('glossary.mutual-fund', { ns: 'glossary' }) as string,
        popupName: 'ai-builder.selectMutualFunds',
        reduxType: AssetTypes.MUTUAL_FUNDS,
      },
    }),
    [t],
  );

  const { name, popupName, reduxType } = assetTypeConfig[assetType];

  const assetsSelected = useAppSelector(state =>
    selectAssetsByType(state, reduxType),
  );

  const watchList = useAppSelector(state => selectWatchlistPositions(state));

  const positions = useMemo(
    () => watchList.filter(position => position.asset_type === assetType),
    [watchList, assetType],
  );

  const tickers = useMemo(
    () =>
      assetsSelected
        .map(({ ticker }) => ticker)
        .filter(ticker => positions.find(position => position.ticker === ticker)),
    [assetsSelected, positions],
  );

  const handleDeleteAsset = useCallback(
    (ticker: string) => {
      dispatch(assetsTypeSlice.actions.deleteAssetType(ticker));
    },
    [dispatch],
  );

  useEffect(() => {
    setSelectedAssetsTickers([]);
  }, [assetType]);

  useEffect(() => {
    const payload = positions
      .filter(position =>
        selectedAssetsTickers.some(
          selectedAsset => selectedAsset.ticker === position.ticker,
        ),
      )
      .map(
        (position): AssetsTypeState => ({
          type: reduxType,
          name: position.name,
          ticker: position.ticker,
          description: position.description ?? '',
        }),
      );

    const unselected = positions
      .filter(({ ticker }) => !payload.find(({ ticker: t }) => t === ticker))
      .map(({ ticker }) => ticker);

    dispatch(assetsTypeSlice.actions.removeAssetTypes(unselected));

    dispatch(assetsTypeSlice.actions.setAssetTypes(payload));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetsTickers]);

  return (
    <SectionContainer
      title={t('ai-builder.select-assets', { ns: 'ai-builder', name }) as string}
      popupName={popupName}
      tooltip
      variant="h3"
      headerClass="mb-[18px]"
    >
      <div className="flex gap-x-4">
        <SearchAsset
          assetType={assetType}
          selectedAssets={assetsSelected}
          placeholder={
            t('ai-builder.search-assets', { ns: 'ai-builder', name }) as string
          }
        />
        <div className="w-[200px]">
          <SelectedAssetsSelector
            selected={tickers}
            onSelectAssets={setSelectedAssetsTickers}
          />
        </div>
      </div>

      <div className="boxwrapper grid_4">
        {assetsSelected.map(({ ticker }) => (
          <AssetItem
            key={ticker}
            ticker={ticker}
            deleteItem={handleDeleteAsset}
            assetType={assetType}
          />
        ))}
      </div>
    </SectionContainer>
  );
};
